/**
 * Favorites Management with Cookies
 * 
 * Manages property favorites using cookies with 3-month expiration.
 * Provides utilities for adding, removing, and retrieving favorites.
 */

const FAVORITES_COOKIE_NAME = 'sunmarbella_favorites';
const COOKIE_EXPIRY_DAYS = 90; // 3 months

/**
 * Get all favorite property IDs from cookie
 */
export function getFavorites(): string[] {
  const cookies = document.cookie.split(';');
  const favoritesCookie = cookies.find(cookie => 
    cookie.trim().startsWith(`${FAVORITES_COOKIE_NAME}=`)
  );
  
  if (!favoritesCookie) {
    return [];
  }
  
  const value = favoritesCookie.split('=')[1];
  if (!value) {
    return [];
  }
  
  try {
    return JSON.parse(decodeURIComponent(value));
  } catch (e) {
    console.error('Failed to parse favorites cookie:', e);
    return [];
  }
}

/**
 * Save favorites array to cookie
 */
function saveFavorites(favorites: string[]): void {
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
  
  const value = encodeURIComponent(JSON.stringify(favorites));
  document.cookie = `${FAVORITES_COOKIE_NAME}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Check if a property is favorited
 */
export function isFavorite(propertyId: string): boolean {
  const favorites = getFavorites();
  return favorites.includes(propertyId);
}

/**
 * Add a property to favorites
 */
export function addFavorite(propertyId: string): void {
  const favorites = getFavorites();
  if (!favorites.includes(propertyId)) {
    favorites.push(propertyId);
    saveFavorites(favorites);
  }
}

/**
 * Remove a property from favorites
 */
export function removeFavorite(propertyId: string): void {
  const favorites = getFavorites();
  const filtered = favorites.filter(id => id !== propertyId);
  saveFavorites(filtered);
}

/**
 * Toggle favorite status for a property
 */
export function toggleFavorite(propertyId: string): boolean {
  const isCurrentlyFavorite = isFavorite(propertyId);
  
  if (isCurrentlyFavorite) {
    removeFavorite(propertyId);
    return false;
  } else {
    addFavorite(propertyId);
    return true;
  }
}

/**
 * Get count of favorites
 */
export function getFavoritesCount(): number {
  return getFavorites().length;
}

/**
 * Clear all favorites
 */
export function clearFavorites(): void {
  document.cookie = `${FAVORITES_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}
